import { LitElement, html, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { style } from 'components/shared/product-tile-configurator/components/tile-property-field.style.js'
import type { ComponentWidgetConfig } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { isFieldVisible, type PropertyFieldSchema } from 'components/shared/product-tile-configurator/widget-property-schema.js'

export interface PropertyChangeDetail {
  key: string
  value: unknown
}

@customElement('csc-tile-property-field')
export class CSCTilePropertyField extends LitElement {
  static override styles = [style]

  @property({ type: Object })
  schema!: PropertyFieldSchema

  @property({ type: Object })
  properties: ComponentWidgetConfig = {}

  private dispatchChange(value: unknown) {
    this.dispatchEvent(new CustomEvent<PropertyChangeDetail>('property-change', {
      detail: { key: this.schema.key, value },
      bubbles: true,
      composed: true,
    }))
  }

  private isVisible(): boolean {
    return isFieldVisible(this.schema, this.properties)
  }

  private renderControl(value: unknown) {
    switch (this.schema.type) {
      case 'boolean':
        return html`
          <cse-toggle
            ?checked=${Boolean(value)}
            @change=${(e: Event) => this.dispatchChange((e.currentTarget as HTMLInputElement & { checked: boolean }).checked)}>
          </cse-toggle>
        `
      case 'enum':
        return html`
          <cse-select
            value=${ifDefined(value as string | undefined)}
            @change=${(e: Event) => this.dispatchChange((e.target as HTMLSelectElement).value)}>
            ${(this.schema.options ?? []).map(option => html`
              <cse-option value=${option.value} ?selected=${option.value === value}>${option.label}</cse-option>
            `)}
          </cse-select>
        `
      case 'hidden-fields':
        return this.renderHiddenFieldsControl(value)
      case 'number':
        return html`
          <cse-stepper-input
            min=${ifDefined(this.schema.min)}
            max=${ifDefined(this.schema.max)}
            step=${ifDefined(this.schema.step ?? 1)}
            value=${ifDefined(value as number | undefined)}
            @change=${(e: Event) => this.dispatchChange(parseFloat((e.currentTarget as HTMLInputElement).value))}>
          </cse-stepper-input>
        `
      case 'string':
      default:
        return html`
          <cse-text-input
            .value=${(value as string) ?? ''}
            @change=${(e: Event) => this.dispatchChange((e.currentTarget as HTMLInputElement).value)}>
          </cse-text-input>
        `
    }
  }

  private renderHiddenFieldsControl(value: unknown) {
    const hidden = new Set(String(value ?? '').split(',').map(entry => entry.trim()).filter(Boolean))
    const toggle = (field: string, checked: boolean) => {
      const next = new Set(hidden)
      if (checked) next.add(field)
      else next.delete(field)
      this.dispatchChange((this.schema.options ?? []).map(option => option.value).filter(entry => next.has(entry)).join(','))
    }
    return html`
      <div class="hidden-fields">
        ${(this.schema.options ?? []).map(option => html`
          <cse-toggle
            data-field=${option.value}
            ?checked=${hidden.has(option.value)}
            @change=${(e: Event) => toggle(option.value, (e.currentTarget as HTMLInputElement & { checked: boolean }).checked)}
          >${option.label}</cse-toggle>
        `)}
      </div>
    `
  }

  private showHelpText(value: unknown): boolean {
    if (!this.schema.helpText) return false
    if (this.schema.helpTextVisibleFor === undefined) return true
    return value === this.schema.helpTextVisibleFor
  }

  override render() {
    if (!this.isVisible()) return nothing

    const value = (this.properties as Record<string, unknown>)[this.schema.key]

    return html`
      <cse-form-field>
        <cse-label slot="label">${this.schema.label}</cse-label>
        ${this.renderControl(value)}
        ${this.showHelpText(value) ? html`<span slot="help-text">${this.schema.helpText}</span>` : nothing}
      </cse-form-field>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-tile-property-field': CSCTilePropertyField
  }
}
